import React from "react";
import { ClipboardCheck, MessageCircle, FileText, GraduationCap } from "lucide-react";

/**
 * 4-step admission timeline — visual, bilingual (HE + AR).
 * Placed under the Hero. No B.Ed language — matches actual college positioning:
 * "המכללה הקהילתית — הכנה והכוונה לתואר ראשון בשנתיים".
 */
export default function AdmissionTimeline({ lang, onStartBot }) {
    const isAr = lang === "ar";

    const steps = [
        {
            icon: ClipboardCheck,
            titleHe: "בגרות 75+",
            titleAr: "بجروت 75+",
            descHe: "בגרות ישראלית ממוצע 75 ומעלה או תוג'יהי ממוצע 65+. חסר עד 3 יחידות? ועדת חריגים.",
            descAr: "بجروت إسرائيلي بمعدل 75 وما فوق أو توجيهي بمعدل 65 وما فوق. ناقص حتى 3 وحدات؟ لجنة استثنائية.",
        },
        {
            icon: MessageCircle,
            titleHe: "שיחת ייעוץ אישית",
            titleAr: "استشارة شخصية",
            descHe: "פגישה קצרה עם יועץ/יועצת לימודים — בערבית ובעברית — כדי לבחור את המסלול המתאים לך.",
            descAr: "لقاء قصير مع مستشار/ة — بالعربية والعبرية — لاختيار المسار المناسب لك شخصياً.",
        },
        {
            icon: FileText,
            titleHe: "רישום מקוון",
            titleAr: "تسجيل عبر الإنترنت",
            descHe: "מילוי טופס פשוט + העלאת תעודות. ליווי אישי בכל צעד. ללא פסיכומטרי.",
            descAr: "تعبئة استمارة بسيطة + رفع الشهادات. مرافقة شخصية بكل خطوة. بدون بسيخومتري.",
        },
        {
            icon: GraduationCap,
            titleHe: "תחילת לימודים",
            titleAr: "بداية الدراسة",
            descHe: "יומיים בשבוע · שנתיים בלבד · במבנה קמפוס הניהול, ראשון לציון. אחרי התואר — מסלולי המשך.",
            descAr: "يومان بالأسبوع · سنتان فقط · في مبنى الإدارة في ريشون لتسيون. بعد اللقب — مسارات مكملة.",
        },
    ];

    return (
        <section className="mb-16 relative" data-testid="admission-timeline">
            {/* Section header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4761F]/10 border border-[#D4761F]/30 text-[#B36318] text-[11px] font-bold uppercase tracking-[0.2em] mb-3">
                    {isAr ? "كيف تنضم؟" : "איך מצטרפים?"}
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0B1F3D]">
                    {isAr ? "طريقك إلينا — 4 خطوات فقط" : "הדרך אלינו — 4 שלבים בלבד"}
                </h2>
                <p className="text-[#475569] mt-2 max-w-xl mx-auto text-sm">
                    {isAr
                        ? "من الاستفسار الأول حتى بداية الدراسة — كل خطوة برفقتنا الشخصية."
                        : "מהפנייה הראשונה עד לפתיחת הלימודים — כל שלב בליווי אישי שלנו."}
                </p>
            </div>

            {/* Timeline — desktop horizontal, mobile vertical */}
            <div className="relative">
                {/* Connector line — desktop only */}
                <div className="hidden md:block absolute top-8 start-[10%] end-[10%] h-0.5 bg-gradient-to-r from-transparent via-[#D4761F]/40 to-transparent z-0" />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4 relative z-10">
                    {steps.map((step, idx) => {
                        const Icon = step.icon;
                        return (
                            <div key={idx} className="relative group" data-testid={`admission-step-${idx + 1}`}>
                                {/* Step number badge */}
                                <div className="absolute -top-2 -start-2 z-20 w-7 h-7 rounded-full bg-gradient-to-br from-[#D4761F] to-[#B36318] text-white text-xs font-bold flex items-center justify-center shadow-md">
                                    {idx + 1}
                                </div>
                                {/* Card */}
                                <div className="bg-white rounded-2xl p-5 pt-6 border border-[#F1E4D2] shadow-[0_6px_20px_rgba(11,31,61,0.06)] group-hover:shadow-[0_12px_28px_rgba(212,118,31,0.18)] group-hover:-translate-y-1 transition-all duration-300 h-full">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0B1F3D] to-[#1A365D] text-[#F8D89C] flex items-center justify-center mb-3 shadow-inner">
                                        <Icon size={26} />
                                    </div>
                                    <h3 className="font-serif text-lg font-bold text-[#0B1F3D] leading-tight mb-1.5">
                                        {isAr ? step.titleAr : step.titleHe}
                                    </h3>
                                    <p className="text-[#475569] text-sm leading-relaxed">
                                        {isAr ? step.descAr : step.descHe}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-8">
                <button
                    onClick={onStartBot}
                    data-testid="admission-start-cta"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0B1F3D] to-[#1A365D] hover:from-[#1A365D] hover:to-[#0B1F3D] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-[0_10px_30px_rgba(11,31,61,0.35)] transition-all hover:scale-105"
                >
                    {isAr ? "ابدأ الآن — استشارة مجانية" : "התחל עכשיו — ייעוץ חינם"}
                </button>
            </div>
        </section>
    );
}
